// src/components/OverworldLightbox.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import RoadmapBadges from "./RoadmapBadges";

/**
 * OverworldLightbox (sequential gating)
 * - Users can open chapter N only if all chapters < N are completed
 * - Chapters 4–10 additionally require premium (unless hasPremium = true)
 *
 * Expects your progression to be stored in localStorage under
 *   TT_PROGRESS_BY_CHAPTER
 * where markChapterComplete(CHAPTER) persists completion.
 */
export default function OverworldLightbox({
  open,
  onClose,
  currentChapter = 1,
  hasPremium = false,
  onLocked, // optional callback for "premium required"
}) {
  const navigate = useNavigate();
  if (!open) return null;

  // ---- helpers: read local completion ----
  const getProgress = () => {
    try {
      return JSON.parse(localStorage.getItem("TT_PROGRESS_BY_CHAPTER") || "{}");
    } catch {
      return {};
    }
  };

  const isCompleted = (n) => {
    const prog = getProgress();
    // support a few possible shapes:
    // { "chapter-1": { complete: true } } OR { "chapter-1": { completed: true } } OR { 1: true }
    const byKey = prog[`chapter-${n}`];
    return !!(
      (byKey && (byKey.complete || byKey.completed)) ||
      prog[n] === true
    );
  };

  const hasFinishedAllBefore = (n) => {
    for (let i = 1; i < n; i += 1) {
      if (!isCompleted(i)) return false;
    }
    return true;
  };

  const needsPremium = (n) => n >= 4 && n <= 10 && !hasPremium;

  // Map node positions (percent of 1600×1000)
  const nodes = [
    { n: 1,  x: 41, y: 75 },
    { n: 2,  x: 18, y: 63 },
    { n: 3,  x: 24, y: 34 },
    { n: 4,  x: 42, y: 18 },
    { n: 5,  x: 78, y: 16 },
    { n: 6,  x: 88, y: 46 },
    { n: 7,  x: 83, y: 68 },
    { n: 8,  x: 87, y: 92 },
    { n: 9,  x: 66, y: 60 },
    { n: 10, x: 56, y: 54 },
  ];

  const handleClick = (n) => {
    // sequential gate first
    if (!hasFinishedAllBefore(n)) {
      // find the first missing prerequisite
      let missing = 1;
      for (let i = 1; i < n; i += 1) {
        if (!isCompleted(i)) { missing = i; break; }
      }
      window.alert(`Finish Chapter ${missing} first to unlock Chapter ${n}.`);
      return;
    }

    // premium gate for 4–10
    if (needsPremium(n)) {
      if (onLocked) onLocked();
      else window.alert("Chapters 4–10 unlock with premium – coming soon!");
      return;
    }

    // okay to go
    navigate(`/chapter-${n}`);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1400] bg-black/60 backdrop-blur-[1px] flex items-center justify-center p-3 sm:p-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Treble Town Map"
    >
      {/* 25% smaller than full size */}
      <div
        className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: "scale(0.75)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close map"
          className="absolute right-3 top-3 z-[2] rounded-md bg-white/90 px-3 py-1 text-sm shadow hover:bg-white"
        >
          ✕
        </button>

        <div className="relative w-full pb-[62.5%]">
          <img
            src="/images/map/overworld.png"
            alt="Treble Town Overworld"
            className="absolute inset-0 w-full h-full object-contain select-none"
            draggable="false"
          />

          {/* ⭐ Numbered star badges overlay (chapters 2–9 → stars 1–8) */}
          <RoadmapBadges currentChapter={currentChapter} />

          {/* Clickable dot nodes (keep behavior & gating as-is) */}
          {nodes.map(({ n, x, y }) => {
            const current = n === currentChapter;
            const past = n < currentChapter && isCompleted(n);

            const ring = current
              ? "ring-2 ring-yellow-400"
              : past
              ? "ring-2 ring-green-400"
              : "ring-1 ring-gray-300";

            return (
              <button
                key={n}
                type="button"
                aria-label={`Chapter ${n}`}
                className={`absolute -translate-x-1/2 -translate-y-1/2
                            w-[18px] h-[18px] rounded-full bg-white/90 shadow
                            ${ring} hover:shadow-md transition`}
                style={{ left: `${x}%`, top: `${y}%` }}
                onClick={() => handleClick(n)}
              >
                {current && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-yellow-400/30" />
                )}
                <span
                  className={`block w-[8px] h-[8px] rounded-full mx-auto my-[5px] ${
                    current ? "bg-yellow-500" : past ? "bg-green-500" : "bg-gray-400"
                  }`}
                />
                {/* lock badge for premium chapters (optional tiny hint) */}
                {needsPremium(n) && (
                  <span className="absolute -right-3 -top-3 text-[12px]">🔒</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
